
import {
  ChangeDetectorRef,
  Component,
  OnInit,
  AfterViewInit,
  ElementRef,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import { CartService } from '../../services/cart';
import { LanguageService } from '../../services/language.service';
import { MapLoaderService } from '../../services/maploader.service';
import { BnNumberPipe } from '../../pipes/bn-number.pipe';

declare const L: any;

@Component({
  selector: 'app-checkout',
  standalone: true,
  imports: [CommonModule, FormsModule, BnNumberPipe],
  templateUrl: './checkout.html',
  styleUrls: ['./checkout.scss']
})
export class Checkout implements OnInit, AfterViewInit {

  @ViewChild('mapContainer')
  mapContainer!: ElementRef<HTMLDivElement>;

  items: any[] = [];

  total: number = 0;

  name = '';
  phone = '';
  address = '';

  payment = 'BKASH';
  paidAmount = 0;

  // "Place Order"-এ ক্লিক করার পর থেকে পেমেন্ট গেটওয়েতে redirect
  // হওয়া পর্যন্ত true — ততক্ষণ বাটন disable থাকবে, একই অর্ডার
  // দুইবার তৈরি হওয়া আটকাবে
  placingOrder = false;

  // LOCATION PICKER STATE
  latitude: number | null = null;
  longitude: number | null = null;
  locationLabel = '';
  locating = false;
  searchQuery = '';
  searchResults: Array<{ label: string; lat: number; lng: number }> = [];

  private map: any = null;
  private marker: any = null;

  // BACKEND URL
  apiUrl =
    'https://superbangladesh-api-1.onrender.com';

  constructor(
    private cart: CartService,
    private router: Router,
    private http: HttpClient,
    public languageService: LanguageService,
    private mapLoader: MapLoaderService,
    private cdr: ChangeDetectorRef
  ) {}


  /* =========================
     IMAGE SUPPORT
  ========================= */
  getImage(url: string): string {

    if (!url) {
      return 'assets/no-image.png';
    }

    // Cloudinary / full URL
    if (url.startsWith('http')) {
      return url;
    }

    // Old Upload Folder
    return `${this.apiUrl}/uploads/${url}`;
  }

  /* =========================
     TRANSLATE
  ========================= */

  t(
    key: string
  ): string {

    return this.languageService
      .translate(key);
  }


  ngOnInit(): void {

    const token = localStorage.getItem('token');

    if (!token) {
      alert(this.t('loginRequiredCheckout'));
      this.router.navigate(['/login']);
      return;
    }

    this.items = this.cart.getItems();

    this.total = this.cart.getTotal();
  }


  ngAfterViewInit(): void {

    this.initMap();
  }


  /* =========================
     INIT MAP (DHAKA DEFAULT)
  ========================= */

  private initMap(): void {

    if (!this.mapContainer) {
      return;
    }

    this.mapLoader
      .load()
      .then((leaflet: any) => {

        const defaultLat = 23.8103;
        const defaultLng = 90.4125;

        this.map = leaflet.map(
          this.mapContainer.nativeElement
        ).setView([defaultLat, defaultLng], 12);

        leaflet.tileLayer(
          'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
          {
            maxZoom: 19,
            attribution: '&copy; OpenStreetMap contributors'
          }
        ).addTo(this.map);

        this.marker = leaflet.marker(
          [defaultLat, defaultLng],
          { draggable: true }
        ).addTo(this.map);

        this.marker.on('dragend', () => {

          const pos = this.marker.getLatLng();

          this.setLocation(pos.lat, pos.lng);
        });

        this.map.on('click', (e: any) => {

          this.marker.setLatLng(e.latlng);

          this.setLocation(e.latlng.lat, e.latlng.lng);
        });
      })
      .catch(() => {
        // map unavailable (e.g. during SSR) — user can still
        // type the address manually, so we just skip the map
      });
  }


  /* =========================
     SET LOCATION + REVERSE GEOCODE
  ========================= */

  private async setLocation(
    lat: number,
    lng: number
  ): Promise<void> {

    this.latitude = lat;
    this.longitude = lng;

    const label =
      await this.mapLoader.reverseGeocode(lat, lng);

    if (label) {

      this.locationLabel = label;

      if (!this.address) {
        this.address = label;
      }
    }

    // ⚠️ ZONELESS FIX: reverseGeocode() uses raw fetch(), which
    // (like navigator.geolocation) runs outside Angular's tracked
    // contexts in this zoneless app — without this the map hint /
    // address never visibly updated even after the data arrived.
    this.cdr.markForCheck();
  }


  /* =========================
     SEARCH LOCATION
  ========================= */

  async onSearchLocation(): Promise<void> {

    this.searchResults =
      await this.mapLoader.search(this.searchQuery);

    // ⚠️ ZONELESS FIX: same reason as setLocation() above.
    this.cdr.markForCheck();
  }

  selectSearchResult(
    result: { label: string; lat: number; lng: number }
  ): void {

    this.searchResults = [];
    this.searchQuery = result.label;

    if (this.map && this.marker) {

      this.map.setView([result.lat, result.lng], 16);
      this.marker.setLatLng([result.lat, result.lng]);
    }

    this.setLocation(result.lat, result.lng);
  }


  /* =========================
     USE MY CURRENT LOCATION
  ========================= */

  useMyLocation(): void {

    if (!navigator.geolocation) {

      alert(this.t('geolocationNotSupported'));
      return;
    }

    this.locating = true;

    navigator.geolocation.getCurrentPosition(
      (pos) => {

        this.locating = false;

        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;

        if (this.map && this.marker) {

          this.map.setView([lat, lng], 16);
          this.marker.setLatLng([lat, lng]);
        }

        this.setLocation(lat, lng);

        // ⚠️ ZONELESS FIX: navigator.geolocation callback runs
        // outside Angular's tracked contexts — without this the
        // "Locating..." button stayed stuck even after finishing.
        this.cdr.markForCheck();
      },
      (err) => {

        this.locating = false;
        this.cdr.markForCheck();

        // TIMEOUT (3) আগে হ্যান্ডল হতো না — কোনো timeout option
        // দেওয়া ছিল না, ফলে মোবাইলে GPS slow/off থাকলে বা
        // permission prompt silently আটকে গেলে callback-ই কখনো
        // fire হতো না, আর "locating..." spinner চিরকাল আটকে থাকত
        // (দেখতে মনে হতো কিছুই হচ্ছে না)।
        if (err && err.code === err.TIMEOUT) {

          alert(
            this.t('geolocationTimeout') ||
            this.t('geolocationDenied')
          );

        } else {

          alert(this.t('geolocationDenied'));
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  }


  /* =========================
     PLACE ORDER
  ========================= */

  placeOrder(): void {

    if (
      !this.name ||
      !this.phone ||
      !this.address
    ) {

      alert(this.t('fillAllFields'));

      return;
    }

    const token = localStorage.getItem('token');

    if (!token) {

      alert(this.t('loginFirst'));

      this.router.navigate(['/login']);

      return;
    }

    if (this.placingOrder) {
      // ডাবল-ক্লিকে দুইটা অর্ডার তৈরি হওয়া আটকানো
      return;
    }

    this.placingOrder = true;

    const orderData = {

      name: this.name,

      phone: this.phone,

      address: this.address,

      latitude: this.latitude,

      longitude: this.longitude,

      totalAmount: this.total + 60,

      paymentMethod: this.payment,

      paidAmount: this.paidAmount,

      items: this.items.map((i: any) => ({

        productId: i.id,

        productName: i.name,

        quantity: i.qty || 1,

        price: i.price
      }))
    };

    const headers = new HttpHeaders({

      Authorization: `Bearer ${token}`

    });

    this.http.post<any>(

      `${this.apiUrl}/api/orders`,

      orderData,

      { headers }

    )
    .subscribe({

      next: (savedOrder) => {

        // COD হলে আগের মতোই — কোনো পেমেন্ট গেটওয়ে লাগে না, অর্ডার
        // সরাসরি কনফার্ম, ডেলিভারিতে টাকা দিতে হবে
        if (this.payment === 'COD') {

          this.cart.clear();

          alert(this.t('orderPlacedSuccess'));

          this.placingOrder = false;

          this.router.navigate(['/orders']);

          return;
        }

        // bKash / Nagad / Card — অর্ডারটা PENDING অবস্থায় তৈরি হয়ে
        // গেছে, এবার SSLCommerz-এ পেমেন্ট session খুলে ইউজারকে সেই
        // পেজে পাঠিয়ে দেওয়া হচ্ছে
        this.redirectToPaymentGateway(savedOrder.id, headers);
      },

      error: (err) => {

        this.placingOrder = false;

        alert(
          err?.error?.message ||
          this.t('orderFailed')
        );
      }
    });
  }

  /* =========================
     REDIRECT TO SSLCOMMERZ
  ========================= */

  private redirectToPaymentGateway(
    orderId: number,
    headers: HttpHeaders
  ): void {

    // ⚠️ SSLCommerz-এর "multi_card_name" প্যারামিটার দিয়ে নির্দিষ্ট
    // গেটওয়েতে (যেমন শুধু bKash) সরাসরি পাঠানো যায়, কিন্তু ভুল/
    // অচেনা ভ্যালু দিলে পুরো initiate call-ই FAILED হয়ে যায় — তাই
    // এখন আর সেটা পাঠাচ্ছি না, SSLCommerz নিজেই bKash/Nagad/Card
    // সব অপশন দেখাবে তাদের নিজস্ব সিলেকশন পেজে
    this.http.post<{ gatewayUrl: string }>(

      `${this.apiUrl}/api/payment/init/${orderId}`,

      {},

      { headers }

    )
    .subscribe({

      next: (res) => {

        this.cart.clear();

        // SSLCommerz-এর নিজস্ব পেমেন্ট পেজে (bKash/Nagad/Card) নিয়ে
        // যাওয়া হচ্ছে — সেখান থেকে পেমেন্ট শেষে সাইটে ফেরত আসবে
        window.location.href = res.gatewayUrl;
      },

      error: (err) => {

        this.placingOrder = false;

        alert(
          err?.error?.message ||
          this.t('orderFailed')
        );
      }
    });
  }
}
